import { useTranslation } from "react-i18next";
import i18n from "i18next";
import informationIcon from "../../../static/assets/information.svg";
import { getCampaignStatus } from "../hooks/useCampaignListData";

const CompletedCheckIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="2" />
    <path
      d="M7.5 12.2L10.4 15.1L16.8 8.7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ScheduledClockIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M6.519 13.333c-1.578 0-2.97-.483-4.175-1.45-1.206-.966-1.98-2.2-2.325-3.7a.634.634 0 01.1-.508.627.627 0 01.45-.258.628.628 0 01.5.133c.144.111.244.261.3.45.3 1.167.925 2.125 1.875 2.875s2.091 1.125 3.325 1.125c1.489 0 2.75-.517 3.783-1.55 1.034-1.033 1.55-2.294 1.55-3.783 0-1.49-.516-2.75-1.55-3.784C9.269 1.85 8.008 1.333 6.519 1.333c-.956 0-1.842.236-2.658.708-.817.473-1.464 1.125-1.942 1.959h1.267c.189 0 .347.064.475.192a.644.644 0 01.192.475.644.644 0 01-.192.475.644.644 0 01-.475.191H.919a.597.597 0 01-.583-.291.558.558 0 01-.1-.625c.466-1.311 1.272-2.375 2.416-3.192C3.797.408 5.086 0 6.519 0a6.75 6.75 0 012.6.525c.811.35 1.517.825 2.117 1.425.6.6 1.075 1.306 1.425 2.117.35.81.525 1.678.525 2.6 0 .922-.175 1.789-.525 2.6-.35.811-.825 1.517-1.425 2.117-.6.6-1.306 1.075-2.117 1.425-.81.35-1.678.524-2.6.524zm.667-6.933l1.666 1.667a.64.64 0 01.184.466.64.64 0 01-.184.467.64.64 0 01-.466.183.64.64 0 01-.467-.183L6.052 7.133a.585.585 0 01-.15-.225.692.692 0 01-.05-.258V4a.644.644 0 01.192-.475.644.644 0 01.475-.192c.189 0 .347.064.475.192A.644.644 0 017.186 4v2.4z"
      fill="currentColor"
    />
  </svg>
);

const STATUS_ALERT_MODIFIERS = {
  Scheduled: "campaign-status-alert--scheduled",
  InProgress: "campaign-status-alert--in-progress",
  Completed: "campaign-status-alert--completed",
};

const CampaignStatusAlert = ({ campaignPeriod, className = "" }) => {
  const { t } = useTranslation();

  if (!campaignPeriod) {
    return null;
  }

  const status = getCampaignStatus(campaignPeriod);
  const isOngoing = status === "InProgress";
  const isScheduled = status === "Scheduled";
  const isCompleted = status === "Completed";

  const startDate = new Date(campaignPeriod.start);
  const stopDate = new Date(campaignPeriod.stop);
  const startYear = startDate.getFullYear();
  const stopYear = stopDate.getFullYear();
  const sameYear = startYear === stopYear;

  const formatDateWithYear = (date, includeYear) => {
    const language = i18n.language || navigator.language;
    const options = {
      month: "long",
      day: "numeric",
      ...(includeYear && { year: "numeric" }),
    };
    let formatted = date.toLocaleDateString(language, options);
    formatted = formatted.replace(/^\w/, (c) => c.toUpperCase());
    formatted = formatted
      .replace(/\bde\b/g, "")
      .replace(/\s+/g, " ")
      .trim();
    return formatted;
  };

  const startDateFormatted = formatDateWithYear(startDate, !sameYear);
  const stopDateFormatted = formatDateWithYear(stopDate, true);
  const durationDays = Math.ceil(
    (stopDate - startDate) / (1000 * 60 * 60 * 24),
  );

  const title = isScheduled
    ? t("campaign.statusAlertScheduled")
    : isOngoing
      ? t("Campaign in progress")
      : t("Campaign completed");

  const statusModifier = STATUS_ALERT_MODIFIERS[status] || "";

  const renderIcon = () => {
    if (isCompleted) {
      return (
        <span className="campaign-status-alert__icon" aria-hidden>
          <CompletedCheckIcon />
        </span>
      );
    }

    if (isScheduled) {
      return (
        <span
          className="campaign-status-alert__icon campaign-status-alert__icon--scheduled"
          aria-hidden
        >
          <ScheduledClockIcon />
        </span>
      );
    }

    return (
      <img
        src={informationIcon}
        alt=""
        className="campaign-status-alert__icon campaign-status-alert__icon--image"
        aria-hidden
      />
    );
  };

  return (
    <div
      className={`campaign-status-alert ${statusModifier} ${className}`.trim()}
      role="status"
    >
      {renderIcon()}
      <div className="campaign-status-alert__text">
        <p className="campaign-status-alert__title">{title}</p>
        <p className="campaign-status-alert__body">
          {t("campaign.statusAlertDateLine", {
            start: startDateFormatted,
            stop: stopDateFormatted,
            days: durationDays,
          })}
        </p>
      </div>
    </div>
  );
};

export default CampaignStatusAlert;
